import React, { useMemo, useState } from 'react';
import { X, User, Users } from 'lucide-react';
import { messageApi } from '../../services/api';
import { Employee, Position, Schedule, StaffMessage, CreateGroupChatRequest } from '../../types';
import BasketSelect from '../BasketSelect';

type Tab = 'direct' | 'group';

interface NewChatDialogProps {
  onClose: () => void;
  onDirectCreated: (thread: StaffMessage) => void;
  onGroupCreated: (data: CreateGroupChatRequest) => Promise<void>;
  employees: Employee[];
  positions: Position[];
  schedules: Schedule[];
  defaultLocationId: number | null;
  employeeLocationIds: Set<number>;
  locations: { id: number; name: string }[];
  currentEmployeeId?: number;
}

const NewChatDialog: React.FC<NewChatDialogProps> = ({
  onClose,
  onDirectCreated,
  onGroupCreated,
  employees,
  positions,
  schedules,
  defaultLocationId,
  employeeLocationIds,
  locations,
  currentEmployeeId,
}) => {
  const [tab, setTab] = useState<Tab>('direct');
  const [locationId, setLocationId] = useState<number | null>(defaultLocationId);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [groupName, setGroupName] = useState('');
  const [pinnedNote, setPinnedNote] = useState('');
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [positionIds, setPositionIds] = useState<number[]>([]);
  const [scheduleIds, setScheduleIds] = useState<number[]>([]);
  const [memberTab, setMemberTab] = useState<'employees' | 'positions' | 'schedules'>('employees');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const employeesAtLocation = useMemo(() => {
    if (!locationId) return employees.filter((e) => e.id !== currentEmployeeId);
    return employees.filter((e) => {
      if (e.id === currentEmployeeId) return false;
      const main = e.profile?.mainLocationId;
      const subs = e.profile?.subLocationIds ?? [];
      return main === locationId || subs.includes(locationId);
    });
  }, [employees, locationId, currentEmployeeId]);

  const schedulesAtLocation = useMemo(() => {
    if (!locationId) return schedules.filter((s) => s.isActive);
    return schedules.filter((s) => s.isActive && (s.locationIds || []).includes(locationId));
  }, [schedules, locationId]);

  const handleDirect = async () => {
    if (!locationId) {
      setError('Select a location');
      return;
    }
    if (!selectedEmployeeId) {
      setError('Select a colleague');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const thread = await messageApi.createDirect({ employeeId: selectedEmployeeId, locationId });
      onDirectCreated(thread);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Could not start direct message');
    } finally {
      setSaving(false);
    }
  };

  const handleGroup = async () => {
    if (!locationId) {
      setError('Select a location');
      return;
    }
    if (!groupName.trim()) {
      setError('Group name is required');
      return;
    }
    const hasMembers = memberIds.length > 0 || positionIds.length > 0 || scheduleIds.length > 0;
    if (!hasMembers) {
      setError('Add at least one member, position, or schedule');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await onGroupCreated({
        title: groupName.trim(),
        pinnedNote: pinnedNote.trim() || undefined,
        locationId,
        assignedToEmployeeIds: memberIds,
        assignedToPositionIds: positionIds.length ? positionIds : undefined,
        assignedToScheduleIds: scheduleIds.length ? scheduleIds : undefined,
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Could not create group');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">New chat</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex border-b border-neutral-200">
          <button
            onClick={() => setTab('direct')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              tab === 'direct' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-neutral-500'
            }`}
          >
            <User className="h-4 w-4" /> Direct message
          </button>
          <button
            onClick={() => setTab('group')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              tab === 'group' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-neutral-500'
            }`}
          >
            <Users className="h-4 w-4" /> Group chat
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
            <select
              value={locationId ?? ''}
              onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            >
              <option value="">Select location</option>
              {locations.filter((l) => employeeLocationIds.has(l.id)).map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">Direct messages are limited to colleagues at the same location.</p>
          </div>

          {tab === 'direct' ? (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Colleague</label>
              <select
                value={selectedEmployeeId ?? ''}
                onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              >
                <option value="">Select colleague...</option>
                {employeesAtLocation.map((e) => (
                  <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.email})</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Group name</label>
                <input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  placeholder="e.g. Thursday handover"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Pinned note (optional)</label>
                <textarea
                  value={pinnedNote}
                  onChange={(e) => setPinnedNote(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  placeholder="Important info visible at top of chat..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Members</label>
                <div className="flex gap-2 mb-2 border-b border-neutral-200">
                  {(['employees', 'positions', 'schedules'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setMemberTab(t)}
                      className={`px-2 py-1 text-xs capitalize ${memberTab === t ? 'text-blue-600 border-b-2 border-blue-600' : 'text-neutral-500'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {memberTab === 'employees' && (
                  <BasketSelect
                    label=""
                    availableItems={employeesAtLocation.map((e) => ({
                      id: e.id,
                      label: `${e.firstName} ${e.lastName}`,
                    }))}
                    selectedIds={memberIds}
                    onChange={(ids) => setMemberIds(ids as number[])}
                    placeholder="Select employees..."
                  />
                )}
                {memberTab === 'positions' && (
                  <BasketSelect
                    label=""
                    availableItems={positions.filter((p) => p.isActive).map((p) => ({ id: p.id, label: p.title }))}
                    selectedIds={positionIds}
                    onChange={(ids) => setPositionIds(ids as number[])}
                    placeholder="Smart group by position..."
                  />
                )}
                {memberTab === 'schedules' && (
                  <BasketSelect
                    label=""
                    availableItems={schedulesAtLocation.map((s) => ({ id: s.id, label: s.name }))}
                    selectedIds={scheduleIds}
                    onChange={(ids) => setScheduleIds(ids as number[])}
                    placeholder="Smart group by schedule..."
                  />
                )}
                <p className="text-xs text-neutral-500 mt-2">
                  Schedule and position groups include all matching staff at this location. New members see full chat history.
                </p>
              </div>
            </>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50">Cancel</button>
            <button
              onClick={tab === 'direct' ? handleDirect : handleGroup}
              disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : tab === 'direct' ? 'Start chat' : 'Create group'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewChatDialog;
